import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Typography,
  Backdrop,
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

// LAP-733 (GE022): labels resolved via t() at render time
const DOMAINS = [
  { value: 'PROFESSIONAL', labelKey: 'catProfessional' },
  { value: 'FINANCE',      labelKey: 'catFinance' },
  { value: 'HEALTH',       labelKey: 'catHealth' },
  { value: 'PERSONAL',     labelKey: 'catPersonalAlt' },
  { value: 'SOCIAL',       labelKey: 'catSocial' },
  { value: 'HOME',         labelKey: 'catHomeEnv' },
  { value: 'ADMIN',        labelKey: 'catAdmin' },
  { value: 'TIME',         labelKey: 'catTime' },
  { value: 'SPIRITUAL',    labelKey: 'catSpiritual' },
  { value: 'SPORTS',       labelKey: 'catSports' },
  { value: 'LEISURE',      labelKey: 'catLeisure' },
  { value: 'FOOTPRINT',    labelKey: 'catFootprint' },
  { value: 'OTHER',        labelKey: 'catOther' },
]

const IDEA_TYPES = [
  { value: 'ACCOMPLISH', labelKey: 'ideaTypeAccomplish' },
  { value: 'BECOME',     labelKey: 'ideaTypeBecome' },
  { value: 'PROGRESS',   labelKey: 'ideaTypeProgress' },
  { value: 'EXPERIENCE', labelKey: 'ideaTypeExperience' },
]

const QUICK_SUGGESTIONS = [
  { emoji: '🌍', titleKey: 'suggTravelWorld',  domain: 'EXPERIENCE',   type: 'EXPERIENCE' },
  { emoji: '💼', titleKey: 'suggCreateBiz',    domain: 'PROFESSIONAL', type: 'ACCOMPLISH' },
  { emoji: '❤️', titleKey: 'suggHealth',       domain: 'HEALTH',       type: 'BECOME' },
  { emoji: '🧠', titleKey: 'suggLearn',        domain: 'PERSONAL',     type: 'PROGRESS' },
  { emoji: '🏠', titleKey: 'suggHomeImprove',  domain: 'HOME',         type: 'ACCOMPLISH' },
]

const STORAGE_KEY = 'roadmap_add_idea_draft'

const AddIdeaPopup = ({ open, onClose, onSubmit, onBack }) => {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  // Load saved data from localStorage
  const getSavedData = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        return JSON.parse(saved)
      }
    } catch (error) {
      console.error('Error loading saved form data:', error)
    }
    return {
      domain: '',
      idea_type: '',
      title: '',
      description: '',
    }
  }

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: getSavedData(),
  })

  const domain = watch('domain')
  const ideaType = watch('idea_type')
  const title = watch('title')
  const description = watch('description')

  // Load saved data when dialog opens
  useEffect(() => {
    if (open) {
      const savedData = getSavedData()
      reset(savedData)
    }
  }, [open, reset])

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    if (open) {
      const formData = {
        domain: domain || '',
        idea_type: ideaType || '',
        title: title || '',
        description: description || '',
      }
      
      // Only save if there's at least some data
      if (formData.domain || formData.idea_type || formData.title || formData.description) {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(formData))
        } catch (error) {
          console.error('Error saving form data:', error)
        }
      }
    }
  }, [open, domain, ideaType, title, description])

  const handleSuggestionClick = (suggestion) => {
    setValue('domain', suggestion.domain)
    setValue('idea_type', suggestion.type)
    setValue('title', suggestion.title)
  }

  const handleFormSubmit = (data) => {
    onSubmit(data)
    // Clear saved data after successful submit
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch (error) {
      console.error('Error clearing saved form data:', error)
    }
    // Reset form after successful submit
    reset({
      domain: '',
      idea_type: '',
      title: '',
      description: '',
    })
  }

  const handleClose = () => {
    // Don't reset form - data is saved in localStorage
    onClose()
  }

  return (
    <>
      <Backdrop
        open={open}
        sx={{
          zIndex: (theme) => theme.zIndex.modal - 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backgroundImage: 'url(/small.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backdropFilter: 'blur(10px)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 29, 0.6)',
          },
        }}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(15, 15, 25, 0.95)',
            backdropFilter: 'blur(30px)',
            borderRadius: '20px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            position: 'relative',
            zIndex: (theme) => theme.zIndex.modal,
          },
        }}
      >
      <DialogTitle
        sx={{
          color: 'white',
          fontSize: { xs: '1.3rem', md: '1.6rem' },
          fontWeight: 600,
          textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.8), 1px 1px 2px rgba(0, 0, 0, 0.8)',
        }}
      >
        {t('roadmap.addIdeaTitle')} ✨
      </DialogTitle>
      <DialogContent
        sx={{
          // Custom scrollbar styling (Word/Google Docs style)
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.5)',
            },
          },
          // Firefox scrollbar
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.05)',
        }}
      >
        <Typography
          variant="body2"
          sx={{ 
            color: 'rgba(255, 255, 255, 0.85)', 
            mb: 3,
            lineHeight: 1.75,
            fontSize: { xs: '0.9rem', md: '1rem' },
          }}
        >
          {t('roadmap.addIdeaSubtitle')}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              {t('roadmap.fieldDomain')}
            </InputLabel>
            <Select
              {...register('domain', { required: t('roadmap.errDomainRequired') })}
              value={domain}
              onChange={(e) => setValue('domain', e.target.value)}
              label={t('roadmap.fieldDomain')}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiSvgIcon-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              }}
            >
              {DOMAINS.map((d) => (
                <MenuItem key={d.value} value={d.value}>
                  {t('roadmap.' + d.labelKey)}
                </MenuItem>
              ))}
            </Select>
            {errors.domain && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {errors.domain.message}
              </Typography>
            )}
          </FormControl>

          <FormControl fullWidth>
            <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              {t('roadmap.ideaType')}
            </InputLabel>
            <Select
              {...register('idea_type', { required: t('roadmap.errTypeRequired') })}
              value={ideaType}
              onChange={(e) => setValue('idea_type', e.target.value)}
              label={t('roadmap.ideaType')}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiSvgIcon-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              }}
            >
              {IDEA_TYPES.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {t('roadmap.' + item.labelKey)}
                </MenuItem>
              ))}
            </Select>
            {errors.idea_type && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {errors.idea_type.message}
              </Typography>
            )}
          </FormControl>

          <TextField
            {...register('title', { required: t('roadmap.errTitleRequired') })}
            label={t('roadmap.fieldTitle')}
            placeholder={t('roadmap.addIdeaPlaceholderTitle')}
            fullWidth
            required
            error={!!errors.title}
            helperText={errors.title?.message}
            sx={{
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiOutlinedInput-root': {
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.7)',
                },
              },
            }}
          />

          <TextField
            {...register('description')}
            label={t('roadmap.fieldDescription')}
            placeholder={t('roadmap.addIdeaPlaceholderWhy')}
            fullWidth
            multiline
            rows={4}
            sx={{
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiOutlinedInput-root': {
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.7)',
                },
              },
            }}
          />

          <Box sx={{ mt: 2 }}>
            <Typography
              variant="body2"
              sx={{ 
                color: 'rgba(255, 255, 255, 0.85)', 
                mb: 1,
                fontWeight: 500,
                fontSize: { xs: '0.9rem', md: '1rem' },
              }}
            >
              {t('roadmap.quickSuggestions')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {QUICK_SUGGESTIONS.map((suggestion, index) => {
                const translatedTitle = t('roadmap.' + suggestion.titleKey)
                return (
                <Chip
                  key={index}
                  label={`${suggestion.emoji} ${translatedTitle}`}
                  onClick={() => handleSuggestionClick({ ...suggestion, title: translatedTitle })}
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    cursor: 'pointer',
                  }}
                />
                )
              })}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        {onBack && (
          <Button 
            onClick={onBack} 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.85)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              },
            }}
            variant="outlined"
          >
            {t('roadmap.back')}
          </Button>
        )}
        <Button
          onClick={handleClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.85)',
            borderColor: 'rgba(255, 255, 255, 0.3)',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
            },
          }}
          variant="outlined"
        >
          {t('roadmap.cancel')}
        </Button>
        <Button
          onClick={handleSubmit(handleFormSubmit)}
          variant="contained"
          sx={{ 
            ml: 1,
            backgroundColor: '#1976d2',
            borderRadius: '6px',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#0d47a1',
            },
          }}
        >
          {t('roadmap.validate')}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  )
}

export default AddIdeaPopup

